import React, { useState, useEffect, useRef } from 'react';
import { Save, X, AlertTriangle, HelpCircle } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
const API_BASE_URL = 'https://ior.org/risk-register/web/api';

const RiskEntryForm = ({ onCancel, onSuccess }) => {
  const navigate = useNavigate();
  const { riskCode } = useParams();

  const { user } = useAuth();
  const token = localStorage.getItem('authToken');

  const formRef = useRef({
    id: '',
    title: '',
    description: '',
    category_id: '',
    likelihood: '',
    impact: '',
    owner_id: user.id || "",
    mitigation_strategy: '',
    contingency_plan: '',
    target_resolution_date: '',
    requires_approval: 'smt',
    notes: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [owners, setOwners] = useState([]);
  const [submitError, setSubmitError] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);



  useEffect(() => {
    const fetchRiskData = async () => {
      if (!riskCode){
        return;
      };

      try {
        setIsLoading(true);
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${API_BASE_URL}/risks.php?id=${riskCode}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        const data = await response.json();

        if (data.success && data.data) {
          // Update formRef values
          Object.keys(formRef.current).forEach(key => {
            if (key === 'target_resolution_date' && data.data[key]) {
              formRef.current[key] = new Date(data.data[key]).toISOString().split('T')[0];
            } else {
              formRef.current[key] = data.data[key] || formRef.current[key];
            }
          });

         

         
        } else {
          throw new Error(data.error || 'Failed to fetch risk data');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRiskData();
  }, [riskCode]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catResponse, usersResponse] = await Promise.all([
          fetch(`${API_BASE_URL}/risk-categories.php`),
          fetch(`${API_BASE_URL}/users.php?role=risk_owner`)
        ]);

        const catData = await catResponse.json();
        const userData = await usersResponse.json();

        if (catData.success) {
          setCategories(catData.data);
        }
        if (userData.success) {
          setOwners(userData.data);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
      }
    };

    fetchData();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    formRef.current[name] = value;
    // Clear error if exists
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const formData = formRef.current;
    
    const requiredFields = {
      title: 'Title',
      description: 'Description',
      category_id: 'Category',
      likelihood: 'Level',
      impact: 'Impact',
      owner_id: 'Owner',
      mitigation_strategy: 'Mitigation strategy'
    };

    Object.keys(requiredFields).forEach(field => {
      if (!formData[field]?.toString().trim()) {
        newErrors[field] = `${requiredFields[field]} is required`;
      }
    });
    
    if (formData.title && formData.title.length > 100) {
      newErrors.title = 'Title must be less than 100 characters';
    }
    
    if (formData.description && formData.description.length > 500) {
      newErrors.description = 'Description must be less than 500 characters';
    }
    
    if (formData.target_resolution_date) {
      const targetDate = new Date(formData.target_resolution_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (targetDate < today) {
        newErrors.target_resolution_date = 'Target date cannot be in the past';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const onCancelHandler = (e) => {
    e.preventDefault();
    navigate("/risks");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/risks.php`, {
        method: riskCode ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formRef.current,
          created_by: user.id,
          status: 'pending',
          risk_code: riskCode
        }),
      });

      const data = await response.json();

      if (data.success) {
        navigate("/risks");
      } else {
        setSubmitError(data.error || 'Failed to create risk');
      }
    } catch (err) {
      setSubmitError('An error occurred while saving the risk');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Define the input field component
  const InputField = ({ type = "text", name, label, error, placeholder, ...props }) => (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      <input
        type={type}
        name={name}
        onChange={handleInputChange}
        className={`mt-1 block w-full rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500 ${
          error ? 'border-red-300' : 'border-gray-300'
        }`}
        placeholder={placeholder}
        {...props}
      />
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  );

  // Define the select field component
  const SelectField = ({ name, label, options, error, defaultValue }) => (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      <select
        name={name}
        onChange={handleInputChange}
        defaultValue = {defaultValue}
        className={`mt-1 block w-full rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500 ${
          error ? 'border-red-300' : 'border-gray-300'
        }`}
      >
        <option value="">Select {label}</option>
        {options.map((option) => (
          <option key={option.value || option.id} value={option.value || option.id}>
            {option.label || option.full_name}
          </option>
        ))}
      </select>
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white shadow-md rounded-lg p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Add New Risk</h2>
          <div className="space-x-2">
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              <Save className="w-4 h-4 mr-2" />
              {isSubmitting ? 'Saving...' : 'Save Risk'}
            </button>
            <button
              onClick={onCancelHandler}
              disabled={isSubmitting}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </button>
          </div>
        </div>

        {submitError && (
          <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-400 text-red-700">
            <div className="flex items-center">
              <AlertTriangle className="w-5 h-5 mr-2" />
              {submitError}
            </div>
          </div>
        )}

        <form className="space-y-6">
          <div className="bg-gray-50 p-4 rounded-md">
            <h3 className="text-lg font-medium mb-4">Basic Information</h3>
            <InputField
              name="title"
              label="Risk Title"
              error={errors.title}
              onChange={handleInputChange}
              defaultValue={formRef.current.title}
              placeholder="Enter a clear, concise title for the risk"
            />
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                name="description"
                defaultValue={formRef.current.description}
                onChange={handleInputChange}
                rows="3"
                className={`mt-1 block w-full rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500 ${
                  errors.description ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Provide a detailed description of the risk"
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-600">{errors.description}</p>
              )}
            </div>

            <SelectField
              name="category_id"
              label="Category"
              options={categories}
              error={errors.category_id}
              defaultValue={formRef.current.category_id}
            />
          </div>

          <div className="bg-gray-50 p-4 rounded-md">
            <h3 className="text-lg font-medium mb-4">Risk Assessment</h3>
            <div className="grid grid-cols-2 gap-4">
              <SelectField
                name="likelihood"
                label="Level"
                options={[
                  { value: '1', label: '1 - Very Low' },
                  { value: '2', label: '2 - Low' },
                  { value: '3', label: '3 - Medium' },
                  { value: '4', label: '4 - High' },
                  { value: '5', label: '5 - Very High' }
                ]}
                error={errors.likelihood}
                defaultValue={formRef.current.likelihood}
              />
              
              <SelectField
                name="impact"
                label="Impact"
                options={[
                  { value: '1', label: '1 - Very Low' },
                  { value: '2', label: '2 - Low' },
                  { value: '3', label: '3 - Medium' },
                  { value: '4', label: '4 - High' },
                  { value: '5', label: '5 - Very High' }
                ]}
                error={errors.impact}
                defaultValue={formRef.current.impact}
              />
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded-md">
            <h3 className="text-lg font-medium mb-4">Management Information</h3>
            <SelectField
              name="owner_id"
              label="Risk Owner"
              options={owners}
              error={errors.owner_id}
              defaultValue={formRef.current.owner_id}
            />
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mitigation Strategy
              </label>
              <textarea
                name="mitigation_strategy"
                onChange={handleInputChange}
                defaultValue={formRef.current.mitigation_strategy}
                rows="3"
                className={`mt-1 block w-full rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500 ${
                  errors.mitigation_strategy ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Describe the steps to be taken to mitigate this risk"
              />
              {errors.mitigation_strategy && (
                <p className="mt-1 text-sm text-red-600">{errors.mitigation_strategy}</p>
              )}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contingency Plan
              </label>
              <textarea
                name="contingency_plan"
                onChange={handleInputChange}
                defaultValue={formRef.current.contingency_plan}
                rows="3"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="Describe the plan if the risk occurs despite mitigation"
              />
            </div>

            <InputField
              type="date"
              name="target_resolution_date"
              label="Target Resolution Date"
              error={errors.target_resolution_date}
              defaultValue={formRef.current.target_resolution_date}
            />
          </div>

          <div className="bg-gray-50 p-4 rounded-md">
            <h3 className="text-lg font-medium mb-4">Additional Information</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                name="notes"
                onChange={handleInputChange}
                defaultValue={formRef.current.notes}
                rows="3"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="Any additional notes or context"
              />
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RiskEntryForm;
